/**
 * TR-909 Rhythm Composer
 * Émulation de la boîte à rythmes Roland TR-909
 * Son plus punchy et agressif que la 808
 */

import * as Tone from 'tone';

export class TR909 {
    constructor(audioEngine, options = {}) {
        this.audioEngine = audioEngine;
        this.performance = options.performance || { lowPower: false };

        // État power (pour économiser les ressources)
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut - organisés par instrument
        this.params = {
            // KICK
            kickLevel: 0.8,
            kickAttack: 0.5,
            kickTune: 60,
            kickDecay: 0.4,
            // SNARE
            snareLevel: 0.8,
            snareTone: 0.5,
            snareTune: 0.5,
            snareSnappy: 0.6,
            // CLAP
            clapLevel: 0.7,
            clapDecay: 0.12,
            // RIMSHOT
            rimshotLevel: 0.7,
            rimshotTune: 0.5,
            // HI-HATS
            hhLevel: 0.7,
            hhDecay: 0.06,
            openHHLevel: 0.7,
            openHHDecay: 0.4,
            // CYMBALS
            rideLevel: 0.6,
            rideTune: 0.5,
            rideDecay: 1.5,
            crashLevel: 0.6,
            crashTune: 0.5,
            crashDecay: 2.5
        };

        this.setupAudio();
    }

    setupAudio() {
        // Connecter au canal du mixer (le mixer gère les sends)
        this.mixerChannel = this.audioEngine.getMixerChannelInput('tr909');

        // Channel master pour tous les drums → mixer
        this.masterChannel = new Tone.Gain(1);
        this.masterChannel.connect(this.mixerChannel);

        // === KICK 909 ===
        this.kickGain = new Tone.Gain(this.params.kickLevel).connect(this.masterChannel);

        this.kickDistortion = new Tone.Distortion({
            distortion: 0.1,
            wet: 0.3
        }).connect(this.kickGain);

        this.kickClick = new Tone.MembraneSynth({
            pitchDecay: 0.005,
            octaves: 8,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.0001,
                decay: 0.02,
                sustain: 0,
                release: 0.01
            }
        }).connect(this.kickDistortion);
        this.kickClick.volume.value = -6;

        this.kickBody = new Tone.MembraneSynth({
            pitchDecay: 0.08,
            octaves: 5,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.001,
                decay: this.params.kickDecay,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.kickDistortion);
        this.kickBody.volume.value = 0;

        // === SNARE 909 ===
        this.snareGain = new Tone.Gain(this.params.snareLevel).connect(this.masterChannel);

        this.snareFilter = new Tone.Filter({
            frequency: 5000,
            type: 'highpass',
            rolloff: -12
        }).connect(this.snareGain);

        this.snareNoise = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: 0.15,
                sustain: 0,
                release: 0.05
            }
        }).connect(this.snareFilter);

        this.snareTone = new Tone.MembraneSynth({
            pitchDecay: 0.015,
            octaves: 5,
            envelope: {
                attack: 0.001,
                decay: 0.12,
                sustain: 0,
                release: 0.05
            }
        }).connect(this.snareGain);
        this.snareTone.volume.value = -6;

        // === CLAP 909 ===
        this.clapGain = new Tone.Gain(this.params.clapLevel).connect(this.masterChannel);

        this.clapFilter = new Tone.Filter({
            frequency: 1500,
            type: 'bandpass',
            Q: 1.5
        }).connect(this.clapGain);

        this.clap = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: this.params.clapDecay,
                sustain: 0,
                release: 0.06
            }
        }).connect(this.clapFilter);

        // === RIMSHOT 909 ===
        this.rimshotGain = new Tone.Gain(this.params.rimshotLevel).connect(this.masterChannel);

        this.rimshot = new Tone.MembraneSynth({
            pitchDecay: 0.01,
            octaves: 3,
            oscillator: { type: 'triangle' },
            envelope: {
                attack: 0.001,
                decay: 0.04,
                sustain: 0,
                release: 0.02
            }
        }).connect(this.rimshotGain);

        // === HI-HAT 909 (closed) ===
        this.hhGain = new Tone.Gain(this.params.hhLevel).connect(this.masterChannel);

        this.hhFilter = new Tone.Filter({
            frequency: 10000,
            type: 'highpass'
        }).connect(this.hhGain);

        this.hhBandpass = new Tone.Filter({
            frequency: 8000,
            type: 'bandpass',
            Q: 1
        }).connect(this.hhFilter);

        this.hihat = new Tone.MetalSynth({
            frequency: 250,
            envelope: {
                attack: 0.001,
                decay: this.params.hhDecay,
                release: 0.01
            },
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 4000,
            octaves: 1.5
        }).connect(this.hhBandpass);

        // === OPEN HI-HAT 909 ===
        this.openHHGain = new Tone.Gain(this.params.openHHLevel).connect(this.masterChannel);

        this.openHHFilter = new Tone.Filter({
            frequency: 10000,
            type: 'highpass'
        }).connect(this.openHHGain);

        this.openHHBandpass = new Tone.Filter({
            frequency: 8000,
            type: 'bandpass',
            Q: 1
        }).connect(this.openHHFilter);

        this.openHihat = new Tone.MetalSynth({
            frequency: 250,
            envelope: {
                attack: 0.001,
                decay: this.params.openHHDecay,
                release: 0.1
            },
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 4000,
            octaves: 1.5
        }).connect(this.openHHBandpass);

        // === RIDE CYMBAL ===
        this.rideGain = new Tone.Gain(this.params.rideLevel).connect(this.masterChannel);

        this.ride = new Tone.MetalSynth({
            frequency: 300,
            envelope: {
                attack: 0.001,
                decay: this.params.rideDecay,
                release: 0.3
            },
            harmonicity: 8,
            modulationIndex: 20,
            resonance: 5000,
            octaves: 2
        }).connect(this.rideGain);

        // === CRASH CYMBAL ===
        this.crashGain = new Tone.Gain(this.params.crashLevel).connect(this.masterChannel);

        this.crash = new Tone.MetalSynth({
            frequency: 350,
            envelope: {
                attack: 0.001,
                decay: this.params.crashDecay,
                release: 0.5
            },
            harmonicity: 10,
            modulationIndex: 25,
            resonance: 4500,
            octaves: 2.5
        }).connect(this.crashGain);
    }

    // --- Méthodes de déclenchement ---

    triggerKick(time) {
        this.kickClick.triggerAttackRelease(200, '32n', time);
        this.kickBody.triggerAttackRelease(this.params.kickTune, '8n', time);
    }

    triggerSnare(time) {
        this.snareNoise.triggerAttackRelease('16n', time);
        this.snareTone.triggerAttackRelease(200 * this.params.snareTune + 100, '16n', time);
    }

    triggerHihat(time) {
        this.hihat.triggerAttackRelease('32n', time);
    }

    triggerOpenHihat(time) {
        this.openHihat.triggerAttackRelease('8n', time);
    }

    triggerClap(time) {
        this.clap.triggerAttackRelease('16n', time);
        this.clap.triggerAttackRelease('32n', time + 0.008);
        this.clap.triggerAttackRelease('32n', time + 0.015);
    }

    triggerRimshot(time) {
        // Tune: 0=1000Hz, 1=2000Hz
        const freq = 1000 + this.params.rimshotTune * 1000;
        this.rimshot.triggerAttackRelease(freq, '32n', time);
    }

    triggerRide(time) {
        // Tune affects frequency: 0=200Hz, 1=400Hz
        this.ride.frequency.value = 200 + this.params.rideTune * 200;
        this.ride.triggerAttackRelease('4n', time);
    }

    triggerCrash(time) {
        // Tune affects frequency: 0=250Hz, 1=450Hz
        this.crash.frequency.value = 250 + this.params.crashTune * 200;
        this.crash.triggerAttackRelease('2n', time);
    }

    // --- Setters pour les paramètres ---

    // KICK
    setKickLevel(value) {
        this.params.kickLevel = value;
        this.kickGain.gain.value = value;
    }
    setKickAttack(value) {
        this.params.kickAttack = value;
        // Click: volume et caractère
        this.kickClick.volume.value = -24 + value * 24;
        this.kickClick.pitchDecay = 0.001 + value * 0.02;
        this.kickClick.octaves = 2 + value * 10;
        // Distortion: plus saturé avec attack élevé
        this.kickDistortion.distortion = 0.05 + value * 0.4;
        this.kickDistortion.wet.value = 0.1 + value * 0.5;
    }
    setKickTune(value) {
        this.params.kickTune = value;
    }
    setKickDecay(value) {
        this.params.kickDecay = value;
        this.kickBody.envelope.decay = value;
    }

    // SNARE
    setSnareLevel(value) {
        this.params.snareLevel = value;
        this.snareGain.gain.value = value;
    }
    setSnareTone(value) {
        this.params.snareTone = value;
        // Tone affecte le mix noise/tone
        this.snareNoise.volume.value = -10 + value * 6; // -10dB à -4dB
    }
    setSnareTune(value) {
        this.params.snareTune = value;
    }
    setSnareSnappy(value) {
        this.params.snareSnappy = value;
        this.snareNoise.envelope.decay = 0.08 + value * 0.12;
    }

    // CLAP
    setClapLevel(value) {
        this.params.clapLevel = value;
        this.clapGain.gain.value = value;
    }
    setClapDecay(value) {
        this.params.clapDecay = value;
        this.clap.envelope.decay = value;
    }

    // RIMSHOT
    setRimshotLevel(value) {
        this.params.rimshotLevel = value;
        this.rimshotGain.gain.value = value;
    }
    setRimshotTune(value) {
        this.params.rimshotTune = value;
    }

    // HI-HAT (closed)
    setHHLevel(value) {
        this.params.hhLevel = value;
        this.hhGain.gain.value = value;
    }
    setHihatDecay(value) {
        this.params.hhDecay = value;
        this.hihat.envelope.decay = value;
    }

    // OPEN HI-HAT
    setOpenHHLevel(value) {
        this.params.openHHLevel = value;
        this.openHHGain.gain.value = value;
    }
    setOpenHHDecay(value) {
        this.params.openHHDecay = value;
        this.openHihat.envelope.decay = value;
    }

    // RIDE
    setRideLevel(value) {
        this.params.rideLevel = value;
        this.rideGain.gain.value = value;
    }
    setRideTune(value) {
        this.params.rideTune = value;
    }
    setRideDecay(value) {
        this.params.rideDecay = value;
        this.ride.envelope.decay = value;
    }

    // CRASH
    setCrashLevel(value) {
        this.params.crashLevel = value;
        this.crashGain.gain.value = value;
    }
    setCrashTune(value) {
        this.params.crashTune = value;
    }
    setCrashDecay(value) {
        this.params.crashDecay = value;
        this.crash.envelope.decay = value;
    }

    // --- Getters / setParams / getParams ---

    getParams() {
        return { ...this.params };
    }

    setParams(params) {
        if (params.kickLevel !== undefined) this.setKickLevel(params.kickLevel);
        if (params.kickAttack !== undefined) this.setKickAttack(params.kickAttack);
        if (params.kickTune !== undefined) this.setKickTune(params.kickTune);
        if (params.kickDecay !== undefined) this.setKickDecay(params.kickDecay);
        if (params.snareLevel !== undefined) this.setSnareLevel(params.snareLevel);
        if (params.snareTone !== undefined) this.setSnareTone(params.snareTone);
        if (params.snareTune !== undefined) this.setSnareTune(params.snareTune);
        if (params.snareSnappy !== undefined) this.setSnareSnappy(params.snareSnappy);
        if (params.clapLevel !== undefined) this.setClapLevel(params.clapLevel);
        if (params.clapDecay !== undefined) this.setClapDecay(params.clapDecay);
        if (params.rimshotLevel !== undefined) this.setRimshotLevel(params.rimshotLevel);
        if (params.rimshotTune !== undefined) this.setRimshotTune(params.rimshotTune);
        if (params.hhLevel !== undefined) this.setHHLevel(params.hhLevel);
        if (params.hhDecay !== undefined) this.setHihatDecay(params.hhDecay);
        if (params.openHHLevel !== undefined) this.setOpenHHLevel(params.openHHLevel);
        if (params.openHHDecay !== undefined) this.setOpenHHDecay(params.openHHDecay);
        if (params.rideLevel !== undefined) this.setRideLevel(params.rideLevel);
        if (params.rideTune !== undefined) this.setRideTune(params.rideTune);
        if (params.rideDecay !== undefined) this.setRideDecay(params.rideDecay);
        if (params.crashLevel !== undefined) this.setCrashLevel(params.crashLevel);
        if (params.crashTune !== undefined) this.setCrashTune(params.crashTune);
        if (params.crashDecay !== undefined) this.setCrashDecay(params.crashDecay);
    }

    // Legacy getters for compatibility
    get kickAttack() { return this.params.kickAttack; }
    get kickTune() { return this.params.kickTune; }
    get snareSnappy() { return this.params.snareSnappy; }
    get snareTune() { return this.params.snareTune; }
    get hhDecay() { return this.params.hhDecay; }

    // --- Power ON/OFF ---

    _startOscillators() {
        // TR909 uses triggered oscillators, no continuous ones to start
    }

    _stopOscillators() {
        // TR909 uses triggered oscillators, no continuous ones to stop
    }

    setPower(on) {
        if (on === this.isPowered) return;

        this.isPowered = on;

        if (on) {
            this.masterChannel.connect(this.mixerChannel);
            if (this.performance.lowPower) {
                this._startOscillators();
            }
        } else {
            this.masterChannel.disconnect();
            if (this.performance.lowPower) {
                this._stopOscillators();
            }
        }
    }

    getPower() {
        return this.isPowered;
    }
}
